import { useEffect, useMemo, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { compressedImageUrl } from './compressedImageUrl';
import { ImagePlaceholder } from './ImagePlaceholder';

export interface ImageProps {
  src: string | null | undefined;
  alt: string;
  /** Wrapper classes — controls size, border-radius, positioning. */
  className?: string;
  /** `<img>` classes — object-fit, positioning, transforms. */
  imgClassName?: string;
  /** Inline styles on the `<img>` (e.g. transform). */
  imgStyle?: CSSProperties;
  /** Custom placeholder; defaults to the standard subtle gradient + icon. */
  fallback?: ReactNode;
  /** `<img>` draggable attr — defaults to false. */
  draggable?: boolean;
  /** Image loading strategy — defaults to `lazy`. */
  loading?: 'lazy' | 'eager';
  /**
   * Target render width in CSS pixels. When `width`+`height` are both set,
   * the src is routed through cdn-cgi with `/rs:fit:W*multiplier:H*multiplier:1`.
   */
  width?: number;
  /** Target render height in CSS pixels (see `width`). */
  height?: number;
  /** CDN JPEG quality (0–100). Default 90. */
  quality?: number;
  /** Hi‑dpi multiplier for the CDN target resolution. Default 2. */
  multiplier?: number;
  /** CDN crop string: `x:y:w:h`. */
  crop?: string;
  /** Optional diagnostic hook. */
  onError?: () => void;
  /** Gets the loaded `<img>`, for callers that need its natural size. */
  onLoad?: (img: HTMLImageElement) => void;
}

/**
 * Universal image renderer with a built-in placeholder + cdn-cgi proxy.
 * Used project-wide so every image gets identical fallback + CDN‑resize
 * behaviour.
 *
 * The wrapper is always `relative overflow-hidden rounded-inherit` —
 * callers pass `className` to size it and set its own border-radius.
 *
 * Internal behaviour:
 *   • `src` empty / null / undefined → render placeholder immediately.
 *   • `src` set → piped through `compressedImageUrl` (resize + q) and
 *     rendered as `<img>`. On `onError`, swap to placeholder keeping the
 *     same wrapper dimensions (no layout shift).
 *   • `onLoad` toggles an opacity fade-in so successful loads don't pop.
 */
// Detect whether the consumer is already positioning the wrapper. If yes,
// we skip our default `relative` — otherwise Tailwind's CSS cascade makes
// `.relative` (emitted after `.absolute` in source order) win over the
// consumer's explicit `absolute`, silently breaking layout inside pills /
// tier cards that want the image to be absolutely positioned.
const POSITION_RE = /\b(?:absolute|fixed|sticky|relative)\b/;

export function Image({
  src,
  alt,
  className = '',
  imgClassName = 'absolute inset-0 h-full w-full object-contain',
  imgStyle,
  fallback,
  draggable = false,
  loading = 'lazy',
  width,
  height,
  quality,
  multiplier,
  crop,
  onError,
  onLoad,
}: ImageProps) {
  const [errored, setErrored] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const imgRef = useRef<HTMLImageElement | null>(null);

  // Reset load/error flags whenever the source changes — without this,
  // swapping `src` (e.g. navigating between packs) leaves `loaded=true`
  // from the previous image, so the fresh `<img>` shows up at full
  // opacity before the browser has finished decoding it. The user sees
  // either the previous image lingering or a flash of nothing.
  useEffect(() => {
    setLoaded(false);
    setErrored(false);
  }, [src]);

  // Cached-image race: if the browser served the image from cache, the
  // `load` event may fire before React attaches `onLoad`. After mount,
  // check `img.complete` and force the loaded flag if the image already
  // decoded. Without this, cached images stay at opacity-0 forever.
  useEffect(() => {
    const el = imgRef.current;
    if (!el) return;
    if (el.complete && el.naturalWidth > 0) {
      setLoaded(true);
    }
    // Re-check on every src change — a freshly-pointed <img> may already be
    // cached/decoded. `imgRef`/`setLoaded` are stable, so `src` is the only dep.
  }, [src]);

  const resolvedSrc = useMemo(
    () =>
      compressedImageUrl({
        url: src,
        ...(width !== undefined ? { width } : {}),
        ...(height !== undefined ? { height } : {}),
        ...(quality !== undefined ? { quality } : {}),
        ...(multiplier !== undefined ? { multiplier } : {}),
        ...(crop !== undefined ? { crop } : {}),
      }),
    [src, width, height, quality, multiplier, crop],
  );

  const showFallback = !resolvedSrc || errored;
  const positionClass = POSITION_RE.test(className) ? '' : 'relative';

  return (
    <div
      className={`${positionClass} overflow-hidden rounded-[inherit] ${className}`.trim()}
    >
      {showFallback ? (fallback ?? <ImagePlaceholder />) : null}
      {resolvedSrc && !errored && (
        <img
          key={resolvedSrc}
          ref={imgRef}
          src={resolvedSrc}
          alt={alt}
          draggable={draggable}
          loading={loading}
          onError={() => {
            setErrored(true);
            onError?.();
          }}
          onLoad={(e) => {
            setLoaded(true);
            onLoad?.(e.currentTarget);
          }}
          className={
            imgClassName +
            ' transition-opacity duration-200 ' +
            (loaded ? 'opacity-100' : 'opacity-0')
          }
          style={imgStyle}
        />
      )}
    </div>
  );
}
